import type {
  BaseProduct,
  Variant,
} from '@bulknutrients/bulk-shared-lib/interfaces/Product';

import { Button } from '@components/Button';
import { AddToCart, AddToCartStatus } from '@components/Button/AddToCart';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { Icons } from '@components/Icons/Icons';
import { Modal } from '@components/Modal/Modal';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { useContext, useEffect, useState } from 'react';

export const FlavourDescriptions = ({
  variants,
  product,
}: {
  variants: Variant[];
  product: BaseProduct;
}) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { addingToCartStatus, setAddingToCartStatus } =
    useContext<UIContextInterface>(UIContext);

  useEffect(() => {
    /* 
      Have to set the status to None when closed otherwise it stays on 'Added'
      causing the green banner to appear everytime the modal reopens
      if a product was previously added
    */
    if (!showModal) setAddingToCartStatus(AddToCartStatus.None);
  }, [showModal]);

  return (
    <>
      <Button
        onClick={(e) => setShowModal(true)}
        buttonStyle="text"
        data-umami-event="hue-flavour-description-button-clicked"
        data-umami-event-productid={variants[0].productId}
      >
        What's this flavour like?
      </Button>
      <Modal
        show={showModal}
        setShow={setShowModal}
        className="max-h-[80vh] max-w-[500px] justify-normal overflow-y-scroll pt-12 dark:bg-black"
      >
        <div
          data-addedtocart={addingToCartStatus === AddToCartStatus.Added}
          className="absolute right-12.5 top-2.5 bg-green p-2 font-bold italic text-white 
          opacity-0 data-[addedtocart=true]:animate-[addToCartConfirmation_5s_0.1s_forwards]"
        >
          <p className="m-0 text-xl font-bold italic">Added to your cart</p>
        </div>
        {variants.map((v) =>
          v.options.length ? (
            <div
              className="align-left mb-2 mt-6 w-full"
              key={v.options[1].label}
            >
              <div className="flex flex-row justify-between items-center gap-2">
                <h3 className="text-2xl font-bold italic text-black dark:text-white">
                  {v.options[1].label}
                </h3>
                <AddToCart
                  buttonStyle="tertiary"
                  variantId={v.variantId}
                  productId={v.productId}
                  sku={v.sku}
                  name={product.name}
                  quantity={1}
                  triggerCartModal={false}
                  customIcon={<Icons.CartPlus size="2rem" />}
                  tooltipText="Quick add to cart"
                  data-umami-event-location="what's this flavour like modal"
                />
              </div>
              <Paragraph className="mt-0">{v.flavourDescription}</Paragraph>
            </div>
          ) : null
        )}
      </Modal>
    </>
  );
};
